import { useEffect, useState, useRef, useMemo } from "react";
import { rafThrottle, startTilt, NO_SENSOR_HELP } from "../lib/tilt";
import { useVehicle } from "../context/VehicleContext";
import { useMeasurements } from "../context/MeasurementsContext";
import { useCamera } from "../hooks/useCamera";
import { Button } from "../components/ui/button";
import { Focus, Camera, Crosshair, Check, RotateCcw, AlertTriangle, Compass, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";

const CORNERS = [
  { key: "FL", label: "Front Left", axle: "front", wheel: "frontLeft" },
  { key: "FR", label: "Front Right", axle: "front", wheel: "frontRight" },
  { key: "RL", label: "Rear Left", axle: "rear", wheel: "rearLeft" },
  { key: "RR", label: "Rear Right", axle: "rear", wheel: "rearRight" },
];

export default function ARAlign() {
  const { specs, vehicleLabel } = useVehicle();
  const { setCornerAngle } = useMeasurements();
  const { videoRef, cameraError, start, stop } = useCamera();
  const [corner, setCorner] = useState("FL");
  const [raw, setRaw] = useState(null);
  const [zero, setZero] = useState(0);
  const [sensorOn, setSensorOn] = useState(false);
  const [fs, setFs] = useState(false);
  const wrapRef = useRef(null);

  const c = CORNERS.find((x) => x.key === corner);
  const spec = c.axle === "front" ? specs?.front?.camber : specs?.rear?.camber;
  const angle = raw == null ? null : +(raw - zero).toFixed(2);
  const inSpec = angle != null && spec && angle >= spec.min && angle <= spec.max;

  useEffect(() => { start(); return stop; }, [start, stop]);

  const stopTiltRef = useRef(null);
  const onOrient = useMemo(() => rafThrottle((e) => {
    if (e.gamma != null) setRaw(Math.round(e.gamma * 100) / 100);
  }), []);

  const enableSensor = async () => {
    try {
      if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
        const res = await DeviceOrientationEvent.requestPermission();
        if (res !== "granted") { toast.error("Motion permission denied"); return; }
      }
      stopTiltRef.current?.();
      stopTiltRef.current = startTilt(onOrient, () => toast.error(NO_SENSOR_HELP.title, NO_SENSOR_HELP));
      setSensorOn(true);
      toast.success("AR sensor active", { description: "Rest the phone flat against the wheel face." });
    } catch { toast.error("Motion sensor unavailable on this device"); }
  };
  useEffect(() => () => { stopTiltRef.current?.(); onOrient.cancel(); }, [onOrient]);

  const apply = () => {
    if (angle == null) { toast.error("Enable the sensor first"); return; }
    setCornerAngle(corner, "camber", angle);
    toast.success(`Applied ${angle >= 0 ? "+" : ""}${angle}° camber to ${c.label}`);
  };

  const color = inSpec ? "#10B981" : "#F59E0B";
  const lineAngle = angle ?? 0;

  const toggleFs = async () => {
    const next = !fs;
    setFs(next);
    try {
      if (next && wrapRef.current?.requestFullscreen) {
        await wrapRef.current.requestFullscreen();
      } else if (!next && document.fullscreenElement && document.exitFullscreen) {
        await document.exitFullscreen();
      }
    } catch { /* fall back to CSS fullscreen */ }
  };

  useEffect(() => {
    const onFsChange = () => { if (!document.fullscreenElement) setFs(false); };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="ar-align-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-lg bg-cyan/10 border border-cyan/20 flex items-center justify-center text-cyan"><Focus size={22} /></div>
        <div>
          <h1 className="font-heading font-bold tracking-tight text-2xl">AR Live Overlay</h1>
          <p className="font-mono text-[11px] text-slate-500 tracking-widest uppercase">{vehicleLabel || "No vehicle"} · live camber on camera</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-4">
          <div className="rounded-md bg-surface border border-steel p-4">
            <div className="font-mono text-[10px] text-slate-400 tracking-widest uppercase mb-3">Select corner</div>
            <div className="grid grid-cols-2 gap-2">
              {CORNERS.map((x) => (
                <button key={x.key} data-testid={`ar-corner-${x.key}`} onClick={() => setCorner(x.key)}
                  className={`py-2.5 rounded-lg font-mono text-xs uppercase tracking-widest transition-all ${corner === x.key ? "bg-cyan text-ink font-bold" : "bg-elevated text-slate-400 hover:text-white border border-steel"}`}>
                  {x.key}
                </button>
              ))}
            </div>
            {spec && (
              <div className="mt-4 font-mono text-[11px] text-slate-400 tracking-widest uppercase flex items-center justify-between">
                <span>Target</span>
                <span className="text-cyan">{spec.target >= 0 ? "+" : ""}{spec.target}° ({spec.min}…{spec.max})</span>
              </div>
            )}
          </div>

          {!sensorOn ? (
            <Button data-testid="ar-enable-sensor" onClick={enableSensor} className="w-full bg-cyan text-ink hover:bg-cyan-hover font-mono text-xs uppercase tracking-widest font-bold">
              <Compass size={15} /> Enable AR Sensor
            </Button>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Button data-testid="ar-zero" onClick={() => setZero(raw ?? 0)} variant="outline" className="bg-elevated border-steel text-slate-300 hover:bg-steel font-mono text-xs uppercase tracking-widest">
                <RotateCcw size={14} /> Set Zero
              </Button>
              <Button data-testid="ar-apply" onClick={apply} className="bg-pass text-ink hover:bg-pass/80 font-mono text-xs uppercase tracking-widest font-bold">
                <Check size={14} /> Apply
              </Button>
            </div>
          )}
          <p className="font-mono text-[10px] text-slate-600 leading-relaxed">
            Hold the phone flat against the wheel face. The green line should overlap the target band when the wheel is in spec. Measure on a level surface for best accuracy.
          </p>
        </div>

        <div className="lg:col-span-8">
          <div
            ref={wrapRef}
            data-testid="ar-camera-wrap"
            className={fs
              ? "fixed inset-0 z-[70] bg-ink overflow-hidden"
              : "relative bg-ink rounded-md border border-steel overflow-hidden"}
            style={fs ? {} : { aspectRatio: "3 / 4", maxHeight: 560 }}
          >
            <div className="absolute inset-0 tactical-grid opacity-30" />
            <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" data-testid="ar-camera" />

            <button
              onClick={toggleFs}
              data-testid="ar-fullscreen-toggle"
              aria-label={fs ? "Exit full screen" : "Full screen"}
              className="absolute top-3 right-3 z-10 flex items-center justify-center w-10 h-10 rounded-sm bg-ink/85 backdrop-blur-sm border border-steel text-cyan hover:bg-steel transition-colors"
            >
              {fs ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>

            {cameraError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-ink/85" data-testid="ar-camera-error">
                <AlertTriangle size={34} className="text-amber" />
                <p className="font-mono text-xs text-slate-300 mt-3 max-w-xs leading-relaxed">{cameraError}</p>
                <Button onClick={start} className="mt-4 bg-amber text-ink font-mono text-xs uppercase tracking-widest font-bold"><Camera size={15} /> Retry</Button>
              </div>
            )}

            {/* AR overlay */}
            <svg viewBox="0 0 300 400" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none">
              <line x1="150" y1="40" x2="150" y2="360" stroke="#64748B" strokeWidth="1.5" strokeDasharray="6 7" />
              <g transform={`rotate(${lineAngle} 150 200)`}>
                <line x1="150" y1="60" x2="150" y2="340" stroke={color} strokeWidth="4" />
                <circle cx="150" cy="60" r="8" fill={color} fillOpacity="0.3" stroke={color} strokeWidth="2" />
                <circle cx="150" cy="340" r="8" fill={color} fillOpacity="0.3" stroke={color} strokeWidth="2" />
              </g>
            </svg>

            <div className="absolute top-3 left-3 rounded-sm bg-ink/85 backdrop-blur-sm border border-steel px-3 py-2" data-testid="ar-readout">
              <div className="font-mono text-[9px] text-slate-400 tracking-widest uppercase">{c.label} · Camber</div>
              <div className="font-mono font-bold text-3xl tabular-nums" style={{ color }}>
                {angle == null ? "--" : `${angle >= 0 ? "+" : ""}${angle.toFixed(2)}`}°
              </div>
              <div className="font-mono text-[9px] tracking-widest" style={{ color: inSpec ? "#10B981" : "#EF4444" }}>
                {angle == null ? "ENABLE SENSOR" : inSpec ? "IN SPEC" : "OUT OF SPEC"}
              </div>
            </div>

            <div className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-sm bg-ink/85 border border-steel px-3 py-1.5 text-cyan font-mono text-[10px] uppercase tracking-widest">
              <Crosshair size={13} /> AR
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
